import type { ColumnDefinition, ColumnTransform, DataSource } from '@/lib/types';

export interface AvailableColumn {
  id: string;
  name: string;
  type: ColumnDefinition['dataType'];
  category: string;
  description?: string;
  required?: boolean;
  transform?: ColumnTransform;
}

export const DATA_SOURCE_COLUMNS: Record<DataSource, AvailableColumn[]> = {
  census: [
    { id: 'mrn', name: 'MRN', type: 'text', category: 'identification', required: true, description: 'Medical Record Number' },
    { id: 'name', name: 'Resident Name', type: 'text', category: 'identification', required: true, description: 'Full resident name' },
    { id: 'residentId', name: 'Resident ID', type: 'text', category: 'identification', description: 'Internal system ID' },
    { id: 'room', name: 'Room Number', type: 'text', category: 'location', description: 'Current room assignment' },
    { id: 'unit', name: 'Unit', type: 'text', category: 'location', description: 'Floor/wing assignment' },
    { id: 'sex', name: 'Sex', type: 'text', category: 'demographics', description: 'M/F/Other' },
    { id: 'dob', name: 'Date of Birth', type: 'date', category: 'demographics', description: 'Birth date' },
    { id: 'age', name: 'Age', type: 'number', category: 'demographics', description: 'Calculated from DOB', transform: 'calculate_age' },
    { id: 'admitDate', name: 'Admit Date', type: 'date', category: 'admission', description: 'Facility admission date' },
    { id: 'lengthOfStay', name: 'Length of Stay', type: 'number', category: 'admission', description: 'Days since admission', transform: 'calculate_duration' },
    { id: 'status', name: 'Census Status', type: 'status', category: 'admission', description: 'Active/Discharged' },
    { id: 'active_on_census', name: 'Active on Census', type: 'boolean', category: 'admission' },
    { id: 'last_seen_census_at', name: 'Last Seen Date', type: 'date', category: 'tracking' },
    { id: 'last_missing_census_at', name: 'Discharge Date', type: 'date', category: 'tracking' },
    { id: 'physician', name: 'Attending Physician', type: 'text', category: 'clinical' },
    { id: 'payor', name: 'Payor', type: 'text', category: 'administrative', description: 'Insurance/payment source' },
    { id: 'notes', name: 'Census Notes', type: 'text', category: 'notes' },
  ],

  abt: [
    { id: 'id', name: 'Record ID', type: 'text', category: 'identification' },
    { id: 'abtCourseId', name: 'ABT Course ID', type: 'text', category: 'identification' },
    { id: 'mrn', name: 'MRN', type: 'text', category: 'identification', required: true },
    { id: 'residentName', name: 'Resident Name', type: 'text', category: 'identification', required: true },
    { id: 'room', name: 'Room', type: 'text', category: 'location' },
    { id: 'unit', name: 'Unit', type: 'text', category: 'location' },
    { id: 'medication', name: 'Medication Name', type: 'text', category: 'medication', required: true },
    { id: 'medicationClass', name: 'Medication Class', type: 'text', category: 'medication', description: 'Drug class (inferred)', transform: 'infer_class' },
    { id: 'dose', name: 'Dose', type: 'text', category: 'medication' },
    { id: 'route', name: 'Route', type: 'text', category: 'medication' },
    { id: 'frequency', name: 'Frequency', type: 'text', category: 'medication' },
    { id: 'indication', name: 'Indication', type: 'text', category: 'clinical', required: true },
    { id: 'infection_source', name: 'Infection Source', type: 'text', category: 'clinical' },
    { id: 'symptomsChecklist', name: 'Symptoms Present', type: 'text', category: 'stewardship', description: 'Clinical symptoms (array)' },
    { id: 'startDate', name: 'Start Date', type: 'date', category: 'timeline', required: true },
    { id: 'endDate', name: 'End Date', type: 'date', category: 'timeline' },
    { id: 'orderDate', name: 'Order Date', type: 'date', category: 'timeline' },
    { id: 'plannedStopDate', name: 'Planned Stop Date', type: 'date', category: 'timeline' },
    { id: 'status', name: 'Status', type: 'status', category: 'tracking', required: true },
    { id: 'stopReason', name: 'Stop Reason', type: 'text', category: 'tracking' },
    { id: 'daysOfTherapy', name: 'Days of Therapy', type: 'number', category: 'metrics', transform: 'calculate_duration' },
    { id: 'nextReviewDate', name: 'Next Review Date', type: 'date', category: 'stewardship' },
    { id: 'prescriber', name: 'Prescriber', type: 'text', category: 'stewardship' },
    { id: 'cultureCollected', name: 'Culture Collected', type: 'boolean', category: 'stewardship' },
    { id: 'cultureResult', name: 'Culture Result', type: 'text', category: 'stewardship' },
    { id: 'cultureReviewedDate', name: 'Culture Reviewed Date', type: 'date', category: 'stewardship' },
    { id: 'timeoutReviewDate', name: 'Timeout Review Date', type: 'date', category: 'stewardship' },
    { id: 'timeoutOutcome', name: 'Timeout Outcome', type: 'status', category: 'stewardship' },
    { id: 'adverseEffects', name: 'Adverse Effects', type: 'text', category: 'stewardship' },
    { id: 'cdiffRisk', name: 'C. diff Risk Level', type: 'status', category: 'stewardship' },
    { id: 'stewardshipNotes', name: 'Stewardship Notes', type: 'text', category: 'stewardship' },
    { id: 'notes', name: 'General Notes', type: 'text', category: 'notes' },
    { id: 'createdAt', name: 'Created Date', type: 'date', category: 'tracking' },
    { id: 'updated_at', name: 'Last Updated', type: 'date', category: 'tracking' },
    { id: 'source', name: 'Data Source', type: 'text', category: 'tracking' },
  ],

  ip_cases: [
    { id: 'id', name: 'Record ID', type: 'text', category: 'identification' },
    { id: 'ipCaseId', name: 'IP Case ID', type: 'text', category: 'identification' },
    { id: 'mrn', name: 'MRN', type: 'text', category: 'identification', required: true },
    { id: 'residentName', name: 'Resident Name', type: 'text', category: 'identification', required: true },
    { id: 'room', name: 'Room', type: 'text', category: 'location' },
    { id: 'unit', name: 'Unit', type: 'text', category: 'location' },
    { id: 'dob', name: 'Date of Birth', type: 'date', category: 'demographics' },
    { id: 'infectionType', name: 'Infection Type', type: 'text', category: 'clinical' },
    { id: 'syndromeCategory', name: 'Syndrome Category', type: 'text', category: 'clinical' },
    { id: 'suspectedOrConfirmedOrganism', name: 'Organism', type: 'text', category: 'clinical' },
    { id: 'protocol', name: 'Precaution Protocol', type: 'status', category: 'precaution', required: true },
    { id: 'isolationType', name: 'Isolation Type', type: 'text', category: 'precaution' },
    { id: 'sourceOfInfection', name: 'Source of Infection', type: 'text', category: 'clinical' },
    { id: 'onsetDate', name: 'Onset Date', type: 'date', category: 'timeline', required: true },
    { id: 'resolutionDate', name: 'Resolution Date', type: 'date', category: 'timeline' },
    { id: 'duration', name: 'Duration (Days)', type: 'number', category: 'metrics', transform: 'calculate_duration' },
    { id: 'status', name: 'Case Status', type: 'status', category: 'tracking', required: true },
    { id: 'nextReviewDate', name: 'Next Review Date', type: 'date', category: 'tracking' },
    { id: 'lastReviewDate', name: 'Last Review Date', type: 'date', category: 'tracking' },
    { id: 'reviewNotes', name: 'Review Notes', type: 'text', category: 'tracking' },
    { id: 'triggerReason', name: 'Trigger Reason', type: 'text', category: 'clinical' },
    { id: 'highContactCare', name: 'High Contact Care', type: 'text', category: 'clinical' },
    { id: 'signagePosted', name: 'Signage Posted', type: 'boolean', category: 'compliance' },
    { id: 'suppliesStocked', name: 'Supplies Stocked', type: 'boolean', category: 'compliance' },
    { id: 'roomCheckDate', name: 'Room Check Date', type: 'date', category: 'compliance' },
    { id: 'exposureLinked', name: 'Exposure Linked', type: 'boolean', category: 'surveillance' },
    { id: 'outbreakId', name: 'Outbreak ID', type: 'text', category: 'surveillance' },
    { id: 'requiredPPE', name: 'Required PPE', type: 'text', category: 'compliance' },
    { id: 'notes', name: 'Case Notes', type: 'text', category: 'notes' },
    { id: 'createdAt', name: 'Created Date', type: 'date', category: 'tracking' },
    { id: '_autoClosed', name: 'Auto-Closed', type: 'boolean', category: 'tracking' },
    { id: '_autoClosedReason', name: 'Auto-Close Reason', type: 'text', category: 'tracking' },
  ],

  vaccinations: [
    { id: 'id', name: 'Record ID', type: 'text', category: 'identification' },
    { id: 'mrn', name: 'MRN', type: 'text', category: 'identification', required: true },
    { id: 'residentName', name: 'Resident Name', type: 'text', category: 'identification', required: true },
    { id: 'room', name: 'Room', type: 'text', category: 'location' },
    { id: 'unit', name: 'Unit', type: 'text', category: 'location' },
    { id: 'vaccine', name: 'Vaccine Type', type: 'text', category: 'vaccine', required: true },
    { id: 'dose', name: 'Dose Number', type: 'text', category: 'vaccine' },
    { id: 'status', name: 'Vaccination Status', type: 'status', category: 'tracking', required: true },
    { id: 'dateGiven', name: 'Date Given', type: 'date', category: 'administration' },
    { id: 'dueDate', name: 'Due Date', type: 'date', category: 'tracking' },
    { id: 'nextDueDate', name: 'Next Due Date', type: 'date', category: 'tracking' },
    { id: 'administrationSource', name: 'Admin Source', type: 'text', category: 'tracking' },
    { id: 'offerDate', name: 'Offer Date', type: 'date', category: 'consent' },
    { id: 'educationProvided', name: 'Education Provided', type: 'boolean', category: 'consent' },
    { id: 'educationDate', name: 'Education Date', type: 'date', category: 'consent' },
    { id: 'educationOutcome', name: 'Education Outcome', type: 'status', category: 'consent' },
    { id: 'manufacturer', name: 'Manufacturer', type: 'text', category: 'vaccine' },
    { id: 'lotNumber', name: 'Lot Number', type: 'text', category: 'vaccine' },
    { id: 'administrationSite', name: 'Administration Site', type: 'text', category: 'administration' },
    { id: 'declineReason', name: 'Decline Reason', type: 'text', category: 'consent' },
    { id: 'medicalExemption', name: 'Medical Exemption', type: 'boolean', category: 'consent' },
    { id: 'contraindication', name: 'Contraindication', type: 'text', category: 'consent' },
    { id: 'consentFormAttached', name: 'Consent Form Attached', type: 'boolean', category: 'consent' },
    { id: 'notes', name: 'Vaccination Notes', type: 'text', category: 'notes' },
  ],

  notes: [
    { id: 'id', name: 'Note ID', type: 'text', category: 'identification' },
    { id: 'noteId', name: 'External Note ID', type: 'text', category: 'identification' },
    { id: 'mrn', name: 'MRN', type: 'text', category: 'identification', required: true },
    { id: 'residentName', name: 'Resident Name', type: 'text', category: 'identification', required: true },
    { id: 'room', name: 'Room', type: 'text', category: 'location' },
    { id: 'unit', name: 'Unit', type: 'text', category: 'location' },
    { id: 'category', name: 'Note Category', type: 'text', category: 'notes' },
    { id: 'text', name: 'Note Text', type: 'text', category: 'notes', required: true },
    { id: 'symptoms', name: 'Symptoms', type: 'text', category: 'clinical' },
    { id: 'symptomCategory', name: 'Symptom Category', type: 'text', category: 'clinical' },
    { id: 'requiresFollowUp', name: 'Requires Follow-Up', type: 'boolean', category: 'tracking' },
    { id: 'followUpDate', name: 'Follow-Up Date', type: 'date', category: 'tracking' },
    { id: 'followUpStatus', name: 'Follow-Up Status', type: 'status', category: 'tracking' },
    { id: 'followUpNotes', name: 'Follow-Up Notes', type: 'text', category: 'tracking' },
    { id: 'providerNotified', name: 'Provider Notified', type: 'boolean', category: 'communication' },
    { id: 'providerNotifiedAt', name: 'Provider Notification Date', type: 'date', category: 'communication' },
    { id: 'providerResponse', name: 'Provider Response', type: 'text', category: 'communication' },
    { id: 'familyNotified', name: 'Family Notified', type: 'boolean', category: 'communication' },
    { id: 'familyNotifiedAt', name: 'Family Notification Date', type: 'date', category: 'communication' },
    { id: 'createdAt', name: 'Created Date', type: 'date', category: 'tracking', required: true },
    { id: 'updatedAt', name: 'Updated Date', type: 'date', category: 'tracking' },
    { id: 'linkedEntityIds', name: 'Linked Records', type: 'text', category: 'tracking' },
  ],

  line_listings: [
    { id: 'id', name: 'Listing ID', type: 'text', category: 'identification' },
    { id: 'lineListingRowId', name: 'Row ID', type: 'text', category: 'identification' },
    { id: 'mrn', name: 'MRN', type: 'text', category: 'identification', required: true },
    { id: 'residentName', name: 'Resident Name', type: 'text', category: 'identification', required: true },
    { id: 'room', name: 'Room', type: 'text', category: 'location' },
    { id: 'unit', name: 'Unit', type: 'text', category: 'location' },
    { id: 'outbreakId', name: 'Outbreak ID', type: 'text', category: 'outbreak', required: true },
    { id: 'onsetDate', name: 'Onset Date', type: 'date', category: 'timeline', required: true },
    { id: 'symptoms', name: 'Symptoms', type: 'text', category: 'clinical' },
    { id: 'symptomCategory', name: 'Symptom Category', type: 'text', category: 'clinical' },
    { id: 'labResults', name: 'Lab Results', type: 'text', category: 'clinical' },
    { id: 'outcome', name: 'Outcome Status', type: 'status', category: 'tracking' },
    { id: 'resolutionDate', name: 'Resolution Date', type: 'date', category: 'timeline' },
    { id: 'notes', name: 'Notes', type: 'text', category: 'notes' },
    { id: 'createdAt', name: 'Created Date', type: 'date', category: 'tracking' },
    { id: 'updatedAt', name: 'Updated Date', type: 'date', category: 'tracking' },
    { id: 'templateData', name: 'Extended Fields', type: 'text', category: 'custom' },
  ],

  outbreaks: [
    { id: 'id', name: 'Outbreak ID', type: 'text', category: 'identification' },
    { id: 'outbreakId', name: 'External Outbreak ID', type: 'text', category: 'identification' },
    { id: 'name', name: 'Outbreak Name', type: 'text', category: 'outbreak', required: true },
    { id: 'type', name: 'Outbreak Type', type: 'text', category: 'outbreak', required: true },
    { id: 'startDate', name: 'Start Date', type: 'date', category: 'timeline', required: true },
    { id: 'endDate', name: 'End Date', type: 'date', category: 'timeline' },
    { id: 'status', name: 'Outbreak Status', type: 'status', category: 'tracking', required: true },
    { id: 'declaredAt', name: 'Declared Date', type: 'date', category: 'timeline' },
    { id: 'resolvedAt', name: 'Resolved Date', type: 'date', category: 'timeline' },
    { id: 'affectedUnits', name: 'Affected Units', type: 'text', category: 'location' },
    { id: 'totalCases', name: 'Total Cases', type: 'number', category: 'metrics' },
  ],


  staff: [
    { id: 'employeeId', name: 'Employee ID', type: 'text', category: 'identification', required: true },
    { id: 'fullName', name: 'Full Name', type: 'text', category: 'identification', required: true },
    { id: 'role', name: 'Role', type: 'text', category: 'position' },
    { id: 'department', name: 'Department', type: 'text', category: 'organization' },
    { id: 'status', name: 'Status', type: 'status', category: 'tracking' },
    { id: 'hireDate', name: 'Hire Date', type: 'date', category: 'timeline' },
    { id: 'notes', name: 'Notes', type: 'text', category: 'notes' },
  ],

  staffVaccination: [
    { id: 'employeeId', name: 'Employee ID', type: 'text', category: 'identification', required: true },
    { id: 'fullName', name: 'Full Name', type: 'text', category: 'identification', required: true },
    { id: 'role', name: 'Role', type: 'text', category: 'position' },
    { id: 'department', name: 'Department', type: 'text', category: 'organization' },
    { id: 'status', name: 'Staff Status', type: 'status', category: 'tracking' },
    { id: 'influenzaStatus', name: 'Influenza Status', type: 'status', category: 'vaccination' },
    { id: 'influenzaLastDate', name: 'Influenza Last Date', type: 'date', category: 'vaccination' },
    { id: 'influenzaLocation', name: 'Influenza Location', type: 'text', category: 'vaccination' },
    { id: 'pneumoStatus', name: 'Pneumococcal Status', type: 'status', category: 'vaccination' },
    { id: 'pneumoLastDate', name: 'Pneumococcal Last Date', type: 'date', category: 'vaccination' },
    { id: 'covidStatus', name: 'COVID-19 Status', type: 'status', category: 'vaccination' },
    { id: 'covidLastDate', name: 'COVID-19 Last Date', type: 'date', category: 'vaccination' },
  ],

  contacts: [
    { id: 'id', name: 'Contact ID', type: 'text', category: 'identification' },
    { id: 'lineListingId', name: 'Line Listing ID', type: 'text', category: 'reference' },
    { id: 'contactType', name: 'Contact Type', type: 'text', category: 'contact' },
    { id: 'contactName', name: 'Contact Name', type: 'text', category: 'contact', required: true },
    { id: 'contactMrn', name: 'Contact MRN', type: 'text', category: 'contact' },
    { id: 'contactRole', name: 'Contact Role', type: 'text', category: 'contact' },
    { id: 'contactUnit', name: 'Contact Unit', type: 'text', category: 'location' },
    { id: 'exposureDate', name: 'Exposure Date', type: 'date', category: 'timeline', required: true },
    { id: 'exposureType', name: 'Exposure Type', type: 'text', category: 'contact' },
    { id: 'notes', name: 'Contact Notes', type: 'text', category: 'notes' },
    { id: 'followUpStatus', name: 'Follow-Up Status', type: 'status', category: 'tracking' },
    { id: 'createdAt', name: 'Created Date', type: 'date', category: 'tracking' },
  ],
};
